import { sommeAuCentime } from '@taki/data-model';
import { estLienParListe } from '@taki/ui';
import type { RollupFieldDef } from '@taki/ui';
import type { FicheGenerique } from './app-declaree.js';

/**
 * LE TOTAL LIE D'UNE FICHE, CALCULE PAR L'HOTE (lot du 14/10/2026) : le
 * « Temps saisi » de chaque tache d'un kanban, somme des durees de ses saisies.
 *
 * POURQUOI ICI, ET PAS UNE REQUETE PAR CARTE. L'ecran declare charge DEJA chacun
 * de ses jeux EN ENTIER (`chargerLesJeux`, aucun plafond : c'est ce qui rend les
 * compteurs du menu justes, regle G15). Le jeu lie est donc en memoire, complet :
 * on le GROUPE une fois par fiche parente, et chaque carte lit son chiffre. Une
 * requete d'agregat par carte ferait des dizaines d'allers-retours pour un
 * resultat identique.
 *
 * CE N'EST PAS UN COMPTE DE PAGE (`docs/DONNEES-SDK.md`) : le jeu n'est pas une
 * page, et quand il n'est PAS charge (`lire` rend `undefined`), le calcul n'a
 * pas lieu et la case reste vide, plutot que d'afficher un nombre faux.
 *
 * LES ARCHIVES NE COMPTENT PAS, comme dans les compteurs : en mode archives le
 * jeu en contient, elles sont ecartees ici. Une fiche sans fiche liee n'a pas de
 * total (`null`, case vide) : « 0 min » sur chaque carte neuve serait du bruit.
 *
 * UN MONTANT SE SOMME AU CENTIME (`auCentime`, `sommeAuCentime`) : additionner
 * des flottants fait 0,1 + 0,2 = 0,30000000000000004, et le total d'un projet
 * de cent lignes derive d'un centime. Une duree ou un nombre se somment tels
 * quels.
 */
export function totalLieDepuisJeu(
  total: Pick<RollupFieldDef, 'agg' | 'field' | 'link'>,
  lire: () => readonly FicheGenerique[] | undefined,
  auCentime = false,
): (fiche: FicheGenerique) => number | null {
  let source: readonly FicheGenerique[] | undefined;
  let groupes = new Map<string, number[]>();
  let parId = new Map<string, FicheGenerique>();
  const parListe = estLienParListe(total.link) ? total.link : null;
  const parChamp = estLienParListe(total.link) ? null : total.link;
  /* La valeur d'UNE fiche liee pour ce total : 1 pour un compte, son champ pour
     une somme ou une moyenne, `null` quand la case est vide (ignoree, comme
     l'agregat du serveur). */
  const valeurDe = (liee: FicheGenerique): number | null => {
    if (total.agg === 'count') return 1;
    const valeur = total.field === undefined ? 1 : lireChemin(liee, total.field);
    return typeof valeur === 'number' && Number.isFinite(valeur) ? valeur : null;
  };
  const vivante = (liee: FicheGenerique): boolean => liee['archivedAt'] == null && liee['deletedAt'] == null;
  const regrouper = (fiches: readonly FicheGenerique[]): Map<string, number[]> => {
    const parParent = new Map<string, number[]>();
    /* LE SENS DIRECT N'A RIEN A GROUPER PAR LE JEU LIE : c'est la liste de
       chaque ligne qui dit quelles fiches elle totalise (voir plus bas). */
    if (parChamp === null) return parParent;
    for (const liee of fiches) {
      if (!vivante(liee)) continue;
      /* L'EGALITE : un parent par fiche. L'APPARTENANCE : CHAQUE ID DE LA LISTE
         de la fiche lie son propre total (un produit cite par trois demandes
         est compte dans les trois), et un id n'y compte qu'une fois. */
      const valeurDuChamp = lireChemin(liee, parChamp.by);
      const parents = parChamp.multiple === true ? idsDeListe(valeurDuChamp) : parentsDeLEgalite(valeurDuChamp);
      if (parents.length === 0) continue;
      const nombre = valeurDe(liee);
      for (const parent of parents) {
        const liste = parParent.get(parent) ?? [];
        /* Un `count` compte la fiche ; une somme ou une moyenne ignore une case
           vide, comme l'agregat du serveur. */
        if (total.agg === 'count') liste.push(1);
        else if (nombre !== null) liste.push(nombre);
        parParent.set(parent, liste);
      }
    }
    return parParent;
  };
  return (fiche) => {
    const fiches = lire();
    if (fiches === undefined) return null;
    if (fiches !== source) {
      source = fiches;
      groupes = regrouper(fiches);
      parId = new Map(fiches.flatMap((liee) => (typeof liee['id'] === 'string' ? [[liee['id'], liee] as const] : [])));
    }
    let valeurs: number[] | undefined;
    if (parListe !== null) {
      /* LE SENS DIRECT : les fiches du jeu lie dont l'id est dans la liste de
         CETTE ligne, vivantes. Un id introuvable dans le jeu charge ne compte pas. */
      const cites = idsDeListe(lireChemin(fiche, parListe.liste));
      valeurs = [];
      for (const id of cites) {
        const liee = parId.get(id);
        if (liee === undefined || !vivante(liee)) continue;
        const nombre = valeurDe(liee);
        if (nombre !== null) valeurs.push(nombre);
      }
    } else {
      const id = fiche['id'];
      valeurs = typeof id === 'string' ? groupes.get(id) : undefined;
    }
    if (valeurs === undefined || valeurs.length === 0) return null;
    const somme = auCentime && total.agg !== 'count'
      ? sommeAuCentime(valeurs)
      : valeurs.reduce((cumul, valeur) => cumul + valeur, 0);
    return total.agg === 'avg' ? somme / valeurs.length : somme;
  };
}

/** Les ids d'un champ liste (un tableau d'ids), sans doublon ni blanc. */
function idsDeListe(valeur: unknown): string[] {
  if (!Array.isArray(valeur)) return [];
  const vus: string[] = [];
  for (const element of valeur) {
    if (typeof element === 'string' && element !== '' && !vus.includes(element)) vus.push(element);
  }
  return vus;
}

/** Le parent d'une egalite : l'id, ou rien. */
function parentsDeLEgalite(valeur: unknown): string[] {
  return typeof valeur === 'string' && valeur !== '' ? [valeur] : [];
}

/** Un chemin de stockage (`taskId`, `customFields.cout`) lu sur un document. */
function lireChemin(fiche: FicheGenerique, chemin: string): unknown {
  return chemin.split('.').reduce<unknown>(
    (valeur, clef) =>
      valeur !== null && typeof valeur === 'object' ? (valeur as Record<string, unknown>)[clef] : undefined,
    fiche,
  );
}
